import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../components/Inputs';
import DarkLoadingButton, {
  defaultButtonStyles,
} from '../../../../../components/V2/Buttons';
import {
  getSectionByIdAsAdmin,
  getSectionsState,
  ISection,
  resetAdminSection,
  SECTION_TYPES,
  TUpdateCourseTeamsSectionAsAdmin,
  TUpdateCourseZoomSectionAsAdmin,
  updateCourseTeamsSectionAsAdmin,
  updateCourseZoomSectionAsAdmin,
} from '../../../../../store/V2/SectionSlice';
import { useAppSelector } from '../../../../../hooks';
import BasicLoader from '../../../../../components/Loaders/BasicLoader';
import { ISelectedOption } from '../../../../../constants';
import { getCourseTutorsState } from '../../../../../store/V2/CourseTutorsSlice';
import { isCourseTutorEligibleForClass } from '../../../../../helper';

interface UpdateClassModalProps {
  open: boolean;
  onClose: () => void;
  sectionId: string;
  onSubmitted: (section: ISection) => void;
}

type TState = {
  title: string;
  selectedTutor: ISelectedOption | null;
};

const initialState: TState = {
  title: '',
  selectedTutor: null,
};

const UpdateClassModal: FunctionComponent<UpdateClassModalProps> = ({
  open,
  sectionId,
  onClose,
  onSubmitted,
}) => {
  const dispatch = useDispatch();

  // Store
  const sectionsState = useAppSelector(getSectionsState);
  const courseTutorsState = useAppSelector(getCourseTutorsState);

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);
  const [tutorOptions, setTutorOptions] = useState<ISelectedOption[]>([]);

  const { adminCourseTutorsData } = courseTutorsState;

  let currentTutorName = '';

  const sectionData = sectionsState.adminSection;
  const isSubmitDisabled = !state.title.trim().length;
  const courseTutors =
    adminCourseTutorsData &&
    adminCourseTutorsData.list &&
    adminCourseTutorsData.list.length
      ? adminCourseTutorsData.list
      : [];

  if (sectionData && sectionData.tutor) {
    const matchingItem = courseTutors.find(
      (item) => item.tutor && item.tutor._id === sectionData.tutor
    );

    if (matchingItem && matchingItem.tutor) {
      const tutorData = matchingItem.tutor;

      currentTutorName = `${tutorData.firstname || ''} ${
        tutorData.lastname || ''
      }`.trim();
    }
  }

  useEffect(() => {
    dispatch(getSectionByIdAsAdmin({ id: sectionId }));

    return () => {
      dispatch(resetAdminSection());
    };
  }, []);

  useEffect(() => {
    if (sectionData) {
      setState((prevState) => {
        const newState = { ...prevState };

        if (sectionData.title) {
          newState.title = sectionData.title;
        }

        return newState;
      });

      if (courseTutors.length) {
        let newTutorOptions: ISelectedOption[] = [];

        courseTutors.forEach((item) => {
          const tutorData = item.tutor;

          const name = `${tutorData.firstname || ''} ${
            tutorData.lastname || ''
          }`.trim();

          const newOption: ISelectedOption = {
            label: name,
            value: tutorData._id,
            courseTutor: item,
          };

          let isEligible = false;

          if (sectionData && sectionData.dateTime && sectionData.duration) {
            isEligible = isCourseTutorEligibleForClass(
              item,
              sectionData.dateTime,
              sectionData.duration || 0
            );
          }

          if (isEligible) {
            newTutorOptions.push(newOption);
          }
        });

        newTutorOptions = newTutorOptions.filter(
          (item) => item.value !== sectionData.tutor
        );

        setTutorOptions([...newTutorOptions]);
      }
    }
  }, [sectionData]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (sectionData && state.title.trim().length) {
      if (sectionData.type === SECTION_TYPES.ZOOM) {
        const apiData: TUpdateCourseZoomSectionAsAdmin = {
          sectionId: sectionData._id,
          title: state.title.trim(),
        };

        if (state.selectedTutor) {
          apiData.tutor = state.selectedTutor.value;
        }

        const res: any = await dispatch(
          updateCourseZoomSectionAsAdmin(apiData)
        );

        if (res && res.payload) {
          onSubmitted(res.payload);
        }
      } else if (sectionData.type === SECTION_TYPES.TEAMS) {
        const apiData: TUpdateCourseTeamsSectionAsAdmin = {
          sectionId: sectionData._id,
          title: state.title.trim(),
        };

        if (state.selectedTutor) {
          apiData.tutor = state.selectedTutor.value;
        }

        const res: any = await dispatch(
          updateCourseTeamsSectionAsAdmin(apiData)
        );

        if (res && res.payload) {
          onSubmitted(res.payload);
        }
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px 30px 20px 30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Update Class
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: { xs: '0 15px', sm: '0 30px' } }}>
        <div role="presentation">
          {sectionsState.status === 'loading' && !sectionData ? (
            <Stack sx={{ p: '32px' }}>
              <BasicLoader />
            </Stack>
          ) : null}
          {sectionData ? (
            <Grid container spacing={2}>
              {currentTutorName ? (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Chip
                      color="default"
                      label={`Current tutor: ${currentTutorName}`}
                      size="small"
                    />
                  </Stack>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                {!currentTutorName ? (
                  <div style={{ minHeight: '6px' }} />
                ) : null}
                <FormControl fullWidth>
                  <TPTextField
                    {...defaultTextFieldProps}
                    fullWidth
                    size="small"
                    label="Class Title *"
                    placeholder="Enter here..."
                    value={state.title}
                    onChange={(e) =>
                      updateStateAttributes({ title: e.target.value })
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Autocomplete
                    size="small"
                    value={state.selectedTutor}
                    options={tutorOptions}
                    popupIcon={TPAutoCompleteArrowIconComponent}
                    onChange={(event, value) =>
                      updateStateAttributes({ selectedTutor: value })
                    }
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...defaultTextFieldProps}
                        size="small"
                        label="Tutor"
                        placeholder="Select"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
          ) : null}
        </div>
      </DialogContent>
      {sectionData ? (
        <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            loading={isLoading}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ ...defaultButtonStyles }}
          >
            Save
          </DarkLoadingButton>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};

export default UpdateClassModal;
